import { pushSafeFirstName } from '@shared/sessions/push-privacy'
import { isNativeApp } from '../../../utils/platform'
import type { ActiveSessionPhase } from '../../../stores/active-session-store'

/**
 * System-level "session in progress" notification (the Google-Maps-style
 * ongoing chip): posted while a session is travelling / arrived / in-session
 * and cleared at check-out. Entirely JS/Capacitor — NO native Java.
 *
 * Android: `ongoing: true` + `autoCancel: false` makes it sticky while the app
 * process is alive (Android 14+ lets the user swipe even "ongoing"
 * notifications — OS behaviour, not fixable from JS). If the OS kills the app
 * process the notification can linger until the app next runs; the store
 * re-syncs/clears it on resume. While the native background-location
 * foreground service is running (travel / checked-in), THAT service owns the
 * one ongoing notification (same `active_session` channel, privacy-safe
 * generic copy) and the store suppresses this chip — see
 * `services/background-location.ts`.
 *
 * iOS: there is no ongoing-notification concept; this is a normal banner that
 * sits in Notification Center until cleared at check-out.
 *
 * Web: no-op — the in-app floating bar covers it.
 */

/** Fixed id — there is only ever ONE active session, updates replace in place. */
const ACTIVE_SESSION_NOTIFICATION_ID = 940_001

const CHANNEL_ID = 'active_session'

async function getLocalNotifications() {
  const { LocalNotifications } = await import('@capacitor/local-notifications')
  // Plain-object wrapper: returning the Capacitor proxy from an async function
  // makes Promise machinery probe `.then` on it, which the bridge rejects.
  return { local: LocalNotifications }
}

let channelReady = false

/** Android 8+ requires a channel; silent/low importance — this is status, not an alert. */
async function ensureChannel(local: Awaited<ReturnType<typeof getLocalNotifications>>['local']) {
  if (channelReady) return
  try {
    await local.createChannel({
      id: CHANNEL_ID,
      name: 'Active session',
      description: 'Shows while a session is in progress',
      importance: 2,
      visibility: 0,
    })
  } catch {
    /* iOS / web: createChannel is unimplemented — fine. */
  }
  channelReady = true
}

const PHASE_TITLES: Record<ActiveSessionPhase, string> = {
  travelling: 'On the way to your session',
  arrived: 'Arrived at your session',
  inSession: 'In session',
}

function formatSince(ts: number): string {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  } catch {
    return ''
  }
}

export async function showActiveSessionNotification(input: {
  sessionUuid: string
  memberName: string
  phase: ActiveSessionPhase
  phaseStartedAt: number
  trackingKm: number | null
}): Promise<void> {
  if (!isNativeApp()) return
  try {
    const { local } = await getLocalNotifications()
    let perm = await local.checkPermissions()
    if (perm.display === 'prompt' || perm.display === 'prompt-with-rationale') {
      perm = await local.requestPermissions()
    }
    if (perm.display !== 'granted') return

    await ensureChannel(local)

    const since = formatSince(input.phaseStartedAt)
    const parts = [pushSafeFirstName(input.memberName)]
    if (since) parts.push(`since ${since}`)
    if (input.trackingKm != null) parts.push(`${input.trackingKm.toFixed(1)} km tracked`)

    await local.schedule({
      notifications: [
        {
          id: ACTIVE_SESSION_NOTIFICATION_ID,
          title: PHASE_TITLES[input.phase],
          body: parts.join(' · '),
          channelId: CHANNEL_ID,
          ongoing: true,
          autoCancel: false,
          extra: { activeSessionUuid: input.sessionUuid },
        },
      ],
    })
  } catch {
    /* plugin unavailable — the in-app banner still covers this */
  }
}

export async function clearActiveSessionNotification(): Promise<void> {
  if (!isNativeApp()) return
  try {
    const { local } = await getLocalNotifications()
    await local.cancel({ notifications: [{ id: ACTIVE_SESSION_NOTIFICATION_ID }] })
  } catch {
    /* plugin unavailable */
  }
}

/**
 * Tap on the notification body → return to the session. Coexists with the
 * incoming-call action listener on the same plugin event: each listener keys
 * off its own `extra` payload and ignores everything else.
 */
export async function addActiveSessionNotificationTapListener(
  onTap: (sessionUuid: string) => void,
): Promise<() => void> {
  if (!isNativeApp()) return () => {}
  try {
    const { local } = await getLocalNotifications()
    const listener = await local.addListener('localNotificationActionPerformed', (event) => {
      const sessionUuid = (event.notification.extra as { activeSessionUuid?: string } | undefined)
        ?.activeSessionUuid
      if (!sessionUuid) return
      if (event.actionId === 'tap') onTap(sessionUuid)
    })
    return () => void listener.remove()
  } catch {
    return () => {}
  }
}
